import { type ReactNode, useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronDown, Presentation } from "@/components/icons"

import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { PresentationBrandSpecPanel } from "./presentation-brand-spec-panel"
import { PresentationGenerationMode } from "./presentation-generation-mode"
import { PresentationTemplatePanel } from "./presentation-template-panel"

function CompanyTemplateFold({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="border-border rounded-lg border">
      <button
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        <div>
          <p className="text-sm font-medium">
            {t("adminDashboard.systemConfig.presentationTemplate.foldTitle", {
              defaultValue: "Company PowerPoint template",
            })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.systemConfig.presentationTemplate.foldHelp", {
              defaultValue:
                "Fallback when the markdown spec is off. Analyze layouts after the .pptx changes.",
            })}
          </p>
        </div>
        <ChevronDown
          className={`size-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? <div className="border-border border-t p-4">{children}</div> : null}
    </div>
  )
}

export function PresentationTemplatesSection() {
  const { t } = useTranslation()

  return (
    <CollapsibleManagementSection
      headerActions={<DelegationModeControl capability="presentation_templates" />}
      icon={<Presentation className="size-5" />}
      id="presentation-templates"
      storageKey="admin-management-presentation-templates-expanded"
      title={t("adminDashboard.systemConfig.presentationTemplate.sectionTitle", {
        defaultValue: "Presentations",
      })}
    >
      <div className="space-y-6">
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.systemConfig.presentationTemplate.sectionDescription", {
            defaultValue:
              "Choose markdown spec, company .pptx, or ask the user. You can keep both files uploaded.",
          })}
        </p>
        <PresentationGenerationMode />
        <PresentationBrandSpecPanel />
        <CompanyTemplateFold>
          <PresentationTemplatePanel />
        </CompanyTemplateFold>
      </div>
    </CollapsibleManagementSection>
  )
}
